import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import MenuItemCard from "../components/MenuItemCard";
import api from "../api/api";
import { motion } from "framer-motion";
import { Home as HomeIcon, MapPin, Phone, X } from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { formatDate, toLocalISODate } from "../utils/helpers";
import {
  formatMealType,
  getCartRestrictionForAddress,
  getBlockedMealTypesForAddress,
  MEAL_TYPES,
} from "../utils/rainAvailability";

function cleanPhone(value) {
  return String(value || "").replace(/[^\d+]/g, "");
}

function formatWhatsappNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 10 ? `91${digits}` : digits;
}

function getContactValues(contact) {
  return ["ContactValue1", "ContactValue2", "ContactValue3"]
    .map((key) => contact?.[key])
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
    .map((value) => String(value).trim());
}

export default function Home() {
   const { user, cart, findMenuItem, isTakeaway, setIsTakeaway, deliveryMode, setDeliveryMode, setUser, setCart } = useApp();
   const [menuData, setMenuData] = useState({});
   const [itemTypes, setItemTypes] = useState([]);
   const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rainRestrictionRows, setRainRestrictionRows] = useState([]);
  const [rainRestrictionContentRows, setRainRestrictionContentRows] = useState([]);
  const [addressRestrictionModal, setAddressRestrictionModal] = useState(null);
  const [timings, setTimings] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => toLocalISODate(new Date()));
  const [contactRows, setContactRows] = useState([]);
  const [multiDaysContent, setMultiDaysContent] = useState({
    text: "Planning a party or need meals for multiple days? We'd love to help - just get in touch with us!",
    whatsappText: "Hi Annam, this is __CUSTOMER_NAME__. I need help with party or multi-day meal planning.",
  });
  const [additionalAddresses, setAdditionalAddresses] = useState([]);
  const [additionalAddressCount, setAdditionalAddressCount] = useState(0);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  // Other menu is hidden for now. Keep the data intact so it can be enabled later.
  const visibleItemTypes = itemTypes.filter((typeObj) => typeObj.type !== "other");
  const whatsappContact = contactRows.find(
    (contact) => String(contact?.ContactType || "").toLowerCase() === "whatsapp"
  );
  const phoneContact = contactRows.find(
    (contact) => String(contact?.ContactType || "").toLowerCase() === "phone"
  );
  const whatsappValue = getContactValues(whatsappContact)[0];
  const phoneValue = getContactValues(phoneContact)[0];
  const customerName = user?.Vname || user?.name || "Customer";
  const whatsappMessage = multiDaysContent.whatsappText
    .replace(/\$\{customerName\}/g, customerName)
    .replace(/__CUSTOMER_NAME__/g, customerName);
  const whatsappHref = whatsappValue
    ? `https://wa.me/${formatWhatsappNumber(whatsappValue)}?text=${encodeURIComponent(whatsappMessage)}`
    : "";
  const phoneHref = phoneValue ? `tel:${cleanPhone(phoneValue)}` : "";
  const todayDate = new Date();
  const tomorrowDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000);
  const dateOptions = [todayDate, tomorrowDate].map((date) => {
    const value = toLocalISODate(date);
    const [year] = value.split("-");
    return {
      value,
      label: `${formatDate(value, {
        weekday: "short",
        day: "numeric",
        month: "short",
      })}, ${year}`,
    };
  });
  const dateStr = selectedDate;
  const selectedDateLabel = formatDate(selectedDate, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const selectedDeliveryAddress = user?.selectedAdditionalAddress;
  const primaryShortName = user?.ShortName?.trim?.() || "Primary";
  const selectedDeliveryAddressName = selectedDeliveryAddress?.ShortName?.trim?.()
    || (selectedDeliveryAddress ? "Selected Address" : primaryShortName);
  const primaryAddressKey = `primary-${user?.Vid || "user"}`;
  const primaryAddress = {
    Vid: user?.Vid,
    ShortName: primaryShortName,
    Address1: user?.Address1 || (!selectedDeliveryAddress ? user?.address : "") || "",
    Address2: user?.Address2 || (!selectedDeliveryAddress ? user?.landmark : "") || "",
    City: user?.City || user?.city || (!selectedDeliveryAddress ? user?.area : "") || "",
    Division: user?.Division || (!selectedDeliveryAddress ? user?.division : "") || "",
    AreaId: user?.AreaId || user?.AreaID || user?.areaId,
    IsCodAvail: user?.IsCodAvail,
  };
  const isPrimarySelected =
    !selectedDeliveryAddress || selectedDeliveryAddress?.selectedKey === primaryAddressKey;

  const getAdditionalAddressKey = (address, index) =>
    `${address.Vid || user?.Vid}-${address.ShortName || "address"}-${index}`;

  const getAdditionalAddressCity = (address) =>
    (address.City || address.Area || "").trim();

  const getAdditionalAddressDivision = (address) =>
    (address.Division || address.Route || "").trim();

  const getAdditionalAddressLine = (address) =>
    [address.Address1 || address.Address || address.AddressLine, address.Address2]
      .filter((part) => part && String(part).trim())
      .join(", ");

  const currentBlockedMealTypes = getBlockedMealTypesForAddress(
    rainRestrictionRows,
    selectedDeliveryAddress || primaryAddress,
    selectedDate
  );

  const getRegularPriceForDivision = (item, division, useSelectedAddressDivision = false) => {
    if (user?.UserType === "New User" && !useSelectedAddressDivision) {
      return item.Circle3Rate || item.MRP || item.StandardCost || 0;
    }

    if (division === "Circle1") return item.Circle1Rate || item.MRP || item.StandardCost || 0;
    if (division === "Circle2") return item.Circle2Rate || item.MRP || item.StandardCost || 0;
    if (division === "Circle3") return item.Circle3Rate || item.MRP || item.StandardCost || 0;
    return item.MRP || item.StandardCost || item.Circle3Rate || 0;
  };

  const persistSelectedUser = (updatedUser) => {
    setUser(updatedUser);
    sessionStorage.setItem("user", JSON.stringify(updatedUser));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setIsAddressModalOpen(false);
  };

  const validateAddressCartItems = async (address) => {
    const cartDates = Object.keys(cart || {});
    if (!cartDates.length) return { affectedItems: [], blockedMealTypes: [] };

    const restrictionRowsByDate = {};
    const restrictionContentRowsByDate = {};
    await Promise.all(
      cartDates.map(async (date) => {
        if (date === selectedDate && rainRestrictionRows.length) {
          restrictionRowsByDate[date] = rainRestrictionRows;
          restrictionContentRowsByDate[date] = rainRestrictionContentRows;
          return;
        }

        const res = await api.GetMenuplan(date);
        restrictionRowsByDate[date] = res?.GetMenuplanjs?.Table3 || [];
        restrictionContentRowsByDate[date] = res?.GetMenuplanjs?.Table4 || [];
      })
    );

    return getCartRestrictionForAddress({
      cart,
      findMenuItem,
      restrictionRowsByDate,
      restrictionContentRowsByDate,
      address,
    });
  };

  const blockAddressChangeWithCartItems = (address, restrictionCheck, updatedUser) => {
    setAddressRestrictionModal({
      addressName: address?.ShortName || "selected address",
      addressArea: getAdditionalAddressCity(address),
      affectedItems: restrictionCheck.affectedItems,
      blockedMealTypes: restrictionCheck.blockedMealTypes,
      wording: restrictionCheck.wording,
      updatedUser,
    });
    setIsAddressModalOpen(false);
  };

  const removeBlockedItemsAndUseAddress = () => {
    if (!addressRestrictionModal?.updatedUser) return;

    const blockedItemKeys = new Set(
      addressRestrictionModal.affectedItems.map((item) => `${item.date}|${item.itemId}`)
    );

    setCart((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([date, itemsObj]) => {
        const keptItems = Object.fromEntries(
          Object.entries(itemsObj || {}).filter(([itemId]) => !blockedItemKeys.has(`${date}|${itemId}`))
        );
        if (Object.keys(keptItems).length) next[date] = keptItems;
      });
      return next;
    });

    persistSelectedUser(addressRestrictionModal.updatedUser);
    setAddressRestrictionModal(null);
  };

  const handleAdditionalAddressSelect = async (address, index) => {
    const selectedDivision = getAdditionalAddressDivision(address);
    const selectedCity = getAdditionalAddressCity(address);
    const selectedAddress = {
      ...address,
      selectedKey: getAdditionalAddressKey(address, index),
      AddressLine: getAdditionalAddressLine(address),
      City: selectedCity,
      Division: selectedDivision,
    };

    const updatedUser = {
      ...user,
      address: selectedAddress.AddressLine,
      landmark: address.Address2?.trim() || "",
      area: selectedCity,
      division: selectedDivision || user?.division || "",
      IsCodAvail: address.IsCodAvail ?? user?.IsCodAvail ?? 0,
      selectedAdditionalAddress: selectedAddress,
    };

    const restrictionCheck = await validateAddressCartItems(selectedAddress);
    if (restrictionCheck.affectedItems.length) {
      blockAddressChangeWithCartItems(selectedAddress, restrictionCheck, updatedUser);
      return;
    }

    persistSelectedUser(updatedUser);
  };

  const handlePrimaryAddressSelect = async () => {
    const primaryLine = getAdditionalAddressLine(primaryAddress);
    const primaryCity = getAdditionalAddressCity(primaryAddress);
    const primaryDivision = getAdditionalAddressDivision(primaryAddress);
    const restrictionCheck = await validateAddressCartItems(primaryAddress);
    const updatedUser = {
      ...user,
      address: primaryLine,
      landmark: primaryAddress.Address2?.trim?.() || "",
      area: primaryCity,
      division: primaryDivision || user?.division || "",
      IsCodAvail: primaryAddress.IsCodAvail ?? user?.IsCodAvail ?? 0,
      selectedAdditionalAddress: null,
    };

    if (restrictionCheck.affectedItems.length) {
      blockAddressChangeWithCartItems(primaryAddress, restrictionCheck, updatedUser);
      return;
    }

    persistSelectedUser(updatedUser);
  };

 useEffect(() => {
   const fetchCustomerData = async () => {
    try {
      if (user?.Vid && user?.UserType) {
        const customerData = await api.GetIndividualCustomerList(user.Vid, user.UserType);
        console.log("GetIndividualCustomerList response:", customerData);

        const customer =
          customerData?.data?.GetIndividualCustomerListjs?.Table?.[0] ||
          customerData?.GetIndividualCustomerListjs?.Table?.[0] ||
          null;
          
        const companyData =
          customerData?.data?.GetIndividualCustomerListjs?.Table1?.[0] ||
          customerData?.GetIndividualCustomerListjs?.Table1?.[0] ||
          null;

        if (customer) {
          const updatedUser = {
            Vid: customer.Vid ?? "",
            Vcode: customer.VCode ?? "",
            name: customer.Vname?.trim() || "",
            mail: customer.Mail?.trim() || "",
            address: user.selectedAdditionalAddress?.AddressLine || customer.Address1?.trim() || "",
            landmark: user.selectedAdditionalAddress?.Address2?.trim() || customer.Address2?.trim() || "", 
            area: user.selectedAdditionalAddress?.City || customer.City?.trim() || "",
            division: user.selectedAdditionalAddress?.Division || customer.Division?.trim() || "",
            Address1: customer.Address1?.trim() || "",
            Address2: customer.Address2?.trim() || "",
            City: customer.City?.trim() || "",
            Division: customer.Division?.trim() || "",
            pin: (customer.Pincode || "").trim(),
            ShortName: customer.ShortName?.trim() || user.ShortName || "",
            UserType: user.UserType,
            IsCodAvail: customer.IsCodAvail ?? user.IsCodAvail ?? 0,
            PinAllowed: customer.PinAllowed,
            selectedAdditionalAddress: user.selectedAdditionalAddress,
            companyAddress: companyData?.ComAddress?.trim() || "No.5/25, Balaji Street,Arumbakkam,Opp. Police Quarters, Taluk Office Road,Sri Venkatesa Perumal Nagar, Chennai -600106.",
            companyName: companyData?.ComName?.trim() || "Hitham Associates Private Limited",
            companyEmail: companyData?.Email?.trim() || "annamatchennai@gmail.com",
            companyState: companyData?.State?.trim() || "TAMILNADU"
          };

          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser));
          sessionStorage.setItem("individualCustomerList", JSON.stringify(customerData));
        }
      }
    } catch (err) {
      console.error("Error fetching individual customer list:", err);
    }
  };

   const fetchMenu = async () => {
     try {
       setLoading(true);
       setError("");

       const res = await api.GetMenuplan(selectedDate);

       const items = res?.GetMenuplanjs?.Table || [];
       const types =
         res?.GetMenuplanjs?.Table1?.map(t => ({
           type: t.Item_Type?.toLowerCase(),
           isOrderAllowed: t.IsOrderAllowed
         })) || [];

       const timingsData = res?.GetMenuplanjs?.Table2?.[0] || {};
       const rainRows = res?.GetMenuplanjs?.Table3 || [];
       const rainContentRows = res?.GetMenuplanjs?.Table4 || [];
       setTimings(timingsData);
       setRainRestrictionRows(rainRows);
       setRainRestrictionContentRows(rainContentRows);

       const grouped = {};
       types.forEach(t => grouped[t.type] = []);

       const map = {};

       items.forEach((item) => {
         const type = item.Item_Type?.toLowerCase();

         if (grouped[type]) grouped[type].push(item);

         const regularPrice = getRegularPriceForDivision(
           item,
           user?.division,
           !!user?.selectedAdditionalAddress
         );

         const takeawayPrice =
           item.TakeAway !== undefined && item.TakeAway !== null
             ? Number(item.TakeAway)
             : Number(regularPrice) || 0;

         const finalPrice = isTakeaway ? takeawayPrice : Number(regularPrice) || 0;

         map[item.ItemId] = {
           id: item.ItemId,
           name: item.Item_Desc1,
           desc2: item.Item_Desc2 ?? "",
           type: type,
           price: finalPrice,
           regularPrice: Number(regularPrice) || 0,
           takeawayPrice,
           isTakeaway,
           raw: item,
         };
       });

       window.__menuItemMap = { ...(window.__menuItemMap || {}), ...map };

       setMenuData(grouped);
       localStorage.setItem("menuData", JSON.stringify(items));

       const sortedTypes = types.sort((a, b) => b.isOrderAllowed - a.isOrderAllowed);
       setItemTypes(sortedTypes);

     } catch (err) {
       console.error("Error fetching menu:", err);
       setError("Failed to load menu.");
     } finally {
       setLoading(false);
     }
   };

   fetchCustomerData();
   fetchMenu();

}, [selectedDate, isTakeaway, user?.division, user?.selectedAdditionalAddress?.selectedKey]);

 useEffect(() => {
   const fetchContactRows = async () => {
     try {
       const res = await api.GetContactUs("ADMIN");
       const rows =
         res?.data?.GetContactUsjs?.Table ||
         res?.GetContactUsjs?.Table ||
         [];
       const multiDaysRow =
         res?.data?.GetContactUsjs?.Table2?.[0] ||
         res?.GetContactUsjs?.Table2?.[0] ||
         null;

       setContactRows(Array.isArray(rows) ? rows : []);
       if (multiDaysRow) {
         setMultiDaysContent((prev) => ({
           text: multiDaysRow.MultiDaysText?.trim?.() || prev.text,
           whatsappText: multiDaysRow.MultiDaysWhatsappTxt?.trim?.() || prev.whatsappText,
         }));
       }
     } catch (err) {
       console.error("Contact fetch failed:", err);
     }
   };

   fetchContactRows();
 }, []);

 useEffect(() => {
   const fetchAdditionalAddresses = async () => {
     if (!user?.Vid) return;

     try {
       const res = await api.GetMultipleAdd(user.Vid);
       const data = res?.data?.GetMultipleAddjs || res?.GetMultipleAddjs || {};
       const count = Number(data?.Table?.[0]?.AddressCnt || 0);
       const addresses = Array.isArray(data?.Table1) ? data.Table1 : [];

       setAdditionalAddressCount(count);
       setAdditionalAddresses(addresses);
     } catch (err) {
       console.error("Error fetching additional addresses:", err);
       setAdditionalAddressCount(0);
       setAdditionalAddresses([]);
     }
   };

   fetchAdditionalAddresses();
 }, [user?.Vid]);

 useEffect(() => {
   if (!isAddressModalOpen) return;

   const handleEscape = (event) => {
     if (event.key === "Escape") {
       setIsAddressModalOpen(false);
     }
   };

   window.addEventListener("keydown", handleEscape);
   return () => window.removeEventListener("keydown", handleEscape);
 }, [isAddressModalOpen]);

 useEffect(() => {
   if (!user?.selectedAdditionalAddress || !user?.division) return;

   const items = Object.values(menuData).flat();
   if (!items.length) return;

   const map = {};
   items.forEach((item) => {
     const regularPrice = getRegularPriceForDivision(item, user.division, true);
     const takeawayPrice =
       item.TakeAway !== undefined && item.TakeAway !== null
         ? Number(item.TakeAway)
         : Number(regularPrice) || 0;

     map[item.ItemId] = {
       id: item.ItemId,
       name: item.Item_Desc1,
       desc2: item.Item_Desc2 ?? "",
       type: item.Item_Type?.toLowerCase(),
       price: isTakeaway ? takeawayPrice : Number(regularPrice) || 0,
       regularPrice: Number(regularPrice) || 0,
       takeawayPrice,
       isTakeaway,
       raw: item,
     };
   });

   window.__menuItemMap = { ...(window.__menuItemMap || {}), ...map };
 }, [user?.selectedAdditionalAddress, user?.division, user?.UserType, isTakeaway, menuData]);

  const renderAddressCard = (address, index, isPrimary = false) => {
    const addressKey = isPrimary ? primaryAddressKey : getAdditionalAddressKey(address, index);
    const addressLine = getAdditionalAddressLine(address);
    const addressCity = getAdditionalAddressCity(address);
    const addressDivision = getAdditionalAddressDivision(address);
    const isSelected = isPrimary
      ? isPrimarySelected
      : selectedDeliveryAddress?.selectedKey === addressKey;

    return (
      <div
        key={addressKey}
        onClick={() => (isPrimary ? handlePrimaryAddressSelect() : handleAdditionalAddressSelect(address, index))}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            isPrimary ? handlePrimaryAddressSelect() : handleAdditionalAddressSelect(address, index);
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        className={`w-full p-2.5 text-left transition-all border shadow-sm rounded-lg ${
          isSelected
            ? "border-red-400 bg-red-50 ring-2 ring-red-100"
            : "border-red-100 bg-red-50/35 hover:border-red-300 hover:bg-white"
        }`}
      >
        <div className="flex items-start gap-2">
          <span className="inline-flex items-center justify-center flex-shrink-0 text-red-600 bg-white border border-red-100 rounded-md shadow-sm w-7 h-7">
            {isPrimary ? <HomeIcon size={14} /> : <MapPin size={14} />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-black text-gray-900">
                {address.ShortName || `Address ${index + 1}`}
              </h4>
              {isSelected && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold text-red-700 bg-white border border-red-200 rounded-full">
                  Selected
                </span>
              )}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-gray-700">
              {addressLine || "Address not available"}
            </p>
            <div className="grid grid-cols-2 gap-1.5 mt-2 text-xs">
              <div className="px-2 py-1.5 bg-white border border-gray-100 rounded-md">
                <span className="block text-[11px] font-semibold text-gray-500">City</span>
                <span className="font-bold text-gray-800">{addressCity || "N/A"}</span>
              </div>
              <div className="px-2 py-1.5 bg-white border border-gray-100 rounded-md">
                <span className="block text-[11px] font-semibold text-gray-500">Division</span>
                <span className="font-bold text-gray-800">{addressDivision || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative min-h-screen pb-20"
         style={{
           background: "transparent"
         }}>
      {/* Animated background elements */}
      <div className="absolute w-40 h-40 rounded-full top-16 right-12 bg-red-300/12 blur-3xl animate-pulse"></div>
      <div className="absolute w-32 h-32 rounded-full top-1/2 left-8 bg-red-300/10 blur-2xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
      <div className="absolute rounded-full bottom-24 right-1/4 w-36 h-36 bg-red-300/8 blur-3xl animate-pulse" style={{animationDelay: '3s'}}></div>
      <div className="absolute rounded-full top-3/4 left-1/2 w-28 h-28 bg-red-400/6 blur-2xl animate-pulse" style={{animationDelay: '4.5s'}}></div>
      {!deliveryMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative w-full max-w-3xl p-5 bg-white shadow-2xl rounded-2xl sm:p-8">
            {/* {additionalAddressCount > 0 && (
              <button
                type="button"
                onClick={() => setIsAddressModalOpen(true)}
                className="absolute inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-red-700 transition-colors bg-white border border-red-200 rounded-full shadow-sm right-4 top-4 hover:bg-red-50 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                aria-label="View additional addresses"
              >
                <HomeIcon size={18} />
                <span className="hidden sm:inline">Addresses</span>
                <span className="flex items-center justify-center min-w-[22px] h-[22px] px-1 text-xs text-white bg-red-600 rounded-full">
                  {additionalAddressCount}
                </span>
              </button>
            )} */}
            <h2 className="text-2xl font-black text-center text-gray-900 sm:text-3xl">Choose Order Type</h2>
            <p className="mt-2 text-center text-gray-600">
             How are you getting your food today? 🍔 
            </p>
             <p className="mt-2 text-center text-gray-600">
            Your meal, your way! 😋
            </p>

            <div className="flex justify-center mt-3">
              <button
                type="button"
                onClick={() => setIsAddressModalOpen(true)}
                className="inline-flex items-center max-w-full gap-2 px-3 py-2 text-sm font-semibold text-gray-800 transition-colors border border-red-100 rounded-full shadow-sm bg-red-50/80 hover:bg-red-100 hover:border-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                aria-label="View delivery addresses"
              >
                <MapPin size={16} className="flex-shrink-0 text-red-600" />
                <span className="text-gray-500">Delivery to:</span>
                <span className="font-black text-red-700 truncate">{selectedDeliveryAddressName}</span>
                <span className="flex items-center justify-center flex-shrink-0 min-w-[20px] h-5 px-1 text-[11px] text-white bg-red-600 rounded-full">
                  {additionalAddressCount}
                </span>
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 mt-6 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setDeliveryMode("takeaway")}
                className="w-full p-6 text-left transition border-2 border-red-200 rounded-2xl bg-red-50 hover:bg-red-100 hover:border-red-300"
              >
              <div className="flex flex-col items-center justify-center gap-2">
  <span className="text-4xl sm:text-5xl md:text-6xl">🛍️</span>
  <span className="text-xl font-extrabold text-red-700 sm:text-2xl">Takeaway</span>
  <span className="mt-1 text-sm text-center text-gray-700 sm:text-base">
  Pick it up fresh and ready from our kitchen.
  </span>
</div>
              </button>

              <button
                type="button"
                onClick={() => setDeliveryMode("delivery")}
                className="w-full p-6 text-left transition bg-white border-2 border-red-200 rounded-2xl hover:bg-red-50 hover:border-red-300"
              >
                <div className="flex flex-col items-center justify-center gap-2">
  <span className="text-4xl sm:text-5xl md:text-6xl">🛵</span>
  <span className="text-xl font-extrabold text-red-700 sm:text-2xl">Delivery</span>
  <span className="mt-1 text-sm text-center text-gray-700 sm:text-base">
  Stay comfy. We'll bring the goodness straight to your door.
  </span>
</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddressModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-xl">
            <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
              <div>
                <p className="text-[10px] font-bold tracking-wide text-red-600 uppercase">Additional Addresses</p>
                <h3 className="text-base font-black text-gray-900">Saved Delivery Addresses</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddressModalOpen(false)}
                className="inline-flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full w-7 h-7 hover:text-red-600 hover:bg-red-50"
                aria-label="Close additional addresses"
              >
                <X size={14} />
              </button>
            </div>

            <div className="p-3 space-y-2 overflow-y-auto max-h-[58vh]">
              {renderAddressCard(primaryAddress, 0, true)}
              {additionalAddresses.map((address, index) => renderAddressCard(address, index))}
            </div>
          </div>
        </div>
      )}
      {addressRestrictionModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm">
          <div className="w-full max-w-2xl overflow-hidden bg-white shadow-2xl rounded-2xl">
            <div className="flex items-center justify-between gap-3 px-5 py-3 text-white bg-red-500">
              <div>
                <p className="text-[11px] font-black tracking-wide uppercase text-white/85">Delivery Restriction</p>
                <h3 className="text-lg font-black">Some cart items cannot be delivered</h3>
              </div>
              <button
                type="button"
                onClick={() => setAddressRestrictionModal(null)}
                className="inline-flex items-center justify-center w-8 h-8 text-white transition-colors rounded-full hover:bg-white/15"
                aria-label="Close delivery warning"
              >
                <X size={17} />
              </button>
            </div>
            <div className="p-3 space-y-3">
              <div className="p-3 border border-red-200 rounded-xl bg-red-50">
                <h4 className="text-base font-black text-red-700">
                  {addressRestrictionModal.wording?.header || "Heavy rain in"} {addressRestrictionModal.addressArea || addressRestrictionModal.addressName}
                </h4>
                <p className="mt-1 text-sm font-medium leading-relaxed text-red-900">
                  {addressRestrictionModal.blockedMealTypes.map(formatMealType).join(", ")} {addressRestrictionModal.wording?.content || "delivery is temporarily suspended for this address. Your selected address and cart were not changed."}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {MEAL_TYPES.map((mealType) => {
                    const isBlocked = addressRestrictionModal.blockedMealTypes.includes(mealType);
                    return (
                      <span
                        key={mealType}
                        className={`px-3 py-1 text-xs font-black rounded-full ${
                          isBlocked ? "text-red-700 bg-red-100" : "text-green-700 bg-green-100"
                        }`}
                      >
                        {isBlocked ? "✕" : "✓"} {formatMealType(mealType)} {isBlocked ? "unavailable" : "available"}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-hidden border border-gray-200 rounded-xl">
                <div className="px-4 py-2 bg-gray-50">
                  <h4 className="font-black text-gray-800">Affected cart items</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {addressRestrictionModal.affectedItems.map((item) => (
                    <div key={`${item.date}-${item.itemId}`} className="flex items-center justify-between gap-3 px-4 py-2 bg-white">
                      <div>
                        <p className="text-sm font-black text-gray-900">{item.name}</p>
                        <p className="text-xs font-semibold text-gray-500">{formatMealType(item.mealType).toUpperCase()} - {formatDate(item.date)} - Qty {item.qty}</p>
                      </div>
                      <p className="text-sm font-black text-gray-900">
                        ₹{(Number(item.price || 0) * Number(item.qty || 0)).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={removeBlockedItemsAndUseAddress}
                  className="w-full px-3 py-2.5 text-sm font-black text-white transition-colors bg-red-500 rounded-xl hover:bg-red-600"
                >
                  Remove {Array.from(new Set(addressRestrictionModal.affectedItems.map((item) => formatMealType(item.mealType)))).join(", ")} Items & Use This Address
                </button>
                <button
                  type="button"
                  onClick={() => setAddressRestrictionModal(null)}
                  className="w-full px-3 py-2.5 text-sm font-black text-gray-700 transition-colors bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
                >
                  Keep Current Address
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddressRestrictionModal(null);
                    setIsAddressModalOpen(true);
                  }}
                  className="w-full px-3 py-2.5 text-sm font-black text-red-700 transition-colors border border-red-200 rounded-xl bg-red-50 hover:bg-red-100"
                >
                  Choose Another Address
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
    <motion.div
  initial={{ opacity: 0, y: -20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, ease: "easeOut" }}
  className="relative px-3 py-2 mb-2 overflow-hidden text-white border shadow-xl bg-gradient-to-br from-red-600 via-red-500 to-red-400 rounded-2xl sm:px-4 sm:py-3 md:px-5 md:py-3 border-white/20"
>
  <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-white/10"></div>
  <div className="absolute w-20 h-20 rounded-full -right-6 -top-8 bg-white/15 blur-2xl sm:w-28 sm:h-28"></div>
  <div className="absolute w-20 h-20 rounded-full -bottom-10 left-1/3 bg-red-900/15 blur-2xl sm:w-28 sm:h-28"></div>

  <div className="relative z-10 flex flex-row items-center justify-between gap-3">
    <div className="min-w-0">
    <h2 className="mb-1 text-sm font-semibold leading-snug sm:text-base md:text-lg drop-shadow-md">
  Welcome to your kitchen... {user?.Vname || user?.name || "Guest"} 👋
    </h2>
  
    </div>

</div>
</motion.div>


     {/* Delivery Unavailable Notification */}
     {user?.PinAllowed === "NotAvail" && (
       <div className="p-3 mx-4 mt-4 border rounded-lg shadow-sm bg-amber-50 border-amber-200">
         <p className="text-sm font-medium text-amber-800">
           Delivery unavailable in your area. Only non-perishable items (snacks, homemade products) available for order.
         </p>
       </div>
     )}

     {/* Meal Type Filter Buttons */}
     <div className="hidden">
      <div className="flex flex-wrap justify-center gap-2 px-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setSelectedType(null)}
          className={`px-4 py-2 rounded-full font-semibold transition-all duration-300 shadow-md ${!selectedType ? 'bg-orange-500 text-white shadow-orange-300' : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'}`}
        >
          🍽️ All
        </motion.button>
        {visibleItemTypes.map(typeObj => {
  const type = typeObj.type;
  const emoji = type === 'breakfast' ? '🌅' : type === 'lunch' ? '🍽️' : type === 'dinner' ? '🌙' : '🍪';
  const isTypeAvailable = typeObj.isOrderAllowed && !currentBlockedMealTypes.has(type);
  return (
    <motion.button
      key={type}
      whileHover={isTypeAvailable ? { scale: 1.03 } : {}}
      whileTap={isTypeAvailable ? { scale: 0.97 } : {}}
      onClick={() => isTypeAvailable && setSelectedType(type)}
      disabled={!isTypeAvailable}
      className={`px-3 py-1.5 rounded-full font-medium text-sm capitalize transition-all duration-200 shadow-sm 
        ${selectedType === type ? 'bg-orange-500 text-white shadow-orange-200' : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'} 
        ${!isTypeAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {emoji} {type}
    </motion.button>
  );
})}
      </div>
    </div>

    {/* Menu Section */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="p-2 mb-3 transition-all border border-red-100 shadow-2xl bg-[#fff4ee]/85 rounded-3xl sm:p-4 md:p-5 sm:mb-8 md:mb-10 hover:shadow-3xl"
      >
        <div className="sticky z-20 px-2 py-1.5 mb-1.5 -mx-2 border-b top-14 bg-white/85 border-red-100/80 backdrop-blur-xl sm:-mx-4 sm:px-4 md:-mx-5 md:px-5">
          <div className="flex flex-col gap-2 px-3 py-1.5 mx-auto border shadow-sm rounded-2xl bg-gradient-to-r from-white via-red-50/80 to-white border-red-100 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start sm:gap-3">
          <h3 className="flex items-center gap-2 pr-1 text-xl font-black text-gray-900 sm:text-2xl md:text-2xl">
            <span className="flex items-center justify-center text-xl text-red-600 shadow-inner w-9 h-9 rounded-xl bg-red-100/80">🍱</span>
            <span>Menu for</span>
            <span className="sr-only">{selectedDateLabel}</span>
          </h3>
          <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
            {dateOptions.map((option) => {
              const isSelected = selectedDate === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSelectedDate(option.value)}
                  className={`px-3.5 py-1.5 text-sm font-bold transition-all border rounded-full sm:text-sm ${
                    isSelected
                      ? "text-white bg-orange-500 border-orange-500 shadow-md shadow-orange-200"
                      : "text-orange-700 bg-white border-orange-200 shadow-sm hover:bg-orange-50 hover:border-orange-300"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-end">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => setSelectedType(null)}
              className={`px-3 py-1.5 rounded-full font-semibold text-sm transition-all duration-300 shadow-sm ${!selectedType ? 'bg-orange-500 text-white shadow-orange-200' : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'}`}
            >
              🍽️ All
            </motion.button>
            {visibleItemTypes.map(typeObj => {
              const type = typeObj.type;
              const emoji = type === 'breakfast' ? '🌅' : type === 'lunch' ? '🍽️' : type === 'dinner' ? '🌙' : '🍪';
              const isTypeAvailable = typeObj.isOrderAllowed && !currentBlockedMealTypes.has(type);
              return (
                <motion.button
                  key={type}
                  whileHover={isTypeAvailable ? { scale: 1.03 } : {}}
                  whileTap={isTypeAvailable ? { scale: 0.97 } : {}}
                  onClick={() => isTypeAvailable && setSelectedType(type)}
                  disabled={!isTypeAvailable}
                  className={`px-3 py-1.5 rounded-full font-medium text-sm capitalize transition-all duration-200 shadow-sm 
                    ${selectedType === type ? 'bg-orange-500 text-white shadow-orange-200' : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'} 
                    ${!isTypeAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {emoji} {type}
                </motion.button>
              );
            })}
          </div>
          </div>
        </div>
<div className="px-2 mb-1.5 sm:px-3">
  <input
    type="text"
    placeholder="🔍 Search for dishes..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    className="w-full p-2.5 text-sm border border-gray-300 shadow-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-red-300"
  />
</div>
<div className="px-2 mb-2 sm:px-3">
  <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 border shadow-sm rounded-lg border-orange-200 bg-orange-50/80">
    <p className="text-xs font-semibold leading-tight text-orange-950 sm:text-sm">
      {currentBlockedMealTypes.size
        ? `${Array.from(currentBlockedMealTypes).map(formatMealType).join(", ")} food is not deliverable today for the selected address.`
        : multiDaysContent.text}
    </p>
    <div className="flex flex-shrink-0 items-center gap-1.5">
      <a
        href={whatsappHref || undefined}
        target={whatsappHref ? "_blank" : undefined}
        rel={whatsappHref ? "noreferrer" : undefined}
        aria-label="WhatsApp Annam"
        className={`inline-flex items-center justify-center w-7 h-7 text-white rounded-full shadow-sm transition-colors ${
          whatsappHref ? "bg-green-500 hover:bg-green-600" : "bg-gray-300 pointer-events-none"
        }`}
      >
        <WhatsAppIcon size={15} />
      </a>
      <a
        href={phoneHref || undefined}
        aria-label="Call Annam"
        className={`inline-flex items-center justify-center w-7 h-7 text-white rounded-full shadow-sm transition-colors ${
          phoneHref ? "bg-red-600 hover:bg-red-700" : "bg-gray-300 pointer-events-none"
        }`}
      >
        <Phone size={15} />
      </a>
    </div>
  </div>
</div>
        {loading ? (
          <div className="py-12 text-center sm:py-16">
            <div className="inline-block w-8 h-8 mb-4 border-4 border-red-300 rounded-full sm:w-10 sm:h-10 border-t-red-600 animate-spin"></div>
            <p className="text-lg text-gray-600 sm:text-xl animate-pulse">Loading delicious menu...</p>
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <p className="text-lg font-semibold text-red-600 sm:text-xl">{error}</p>
          </div>
        ) : (
          (() => {
            const typesToShow = selectedType
              ? visibleItemTypes.filter(t => t.type === selectedType)
              : visibleItemTypes;
            return typesToShow.map((typeObj, i) =>
              menuData[typeObj.type] && menuData[typeObj.type].length ? (
              <motion.div
                key={typeObj.type}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.15, duration: 0.6 }}
                className="mb-6 sm:mb-8"
              >
                {/* Section Header */}
                <div className="flex items-center justify-center mb-2 sm:mb-3 sm:justify-start">
                  <span className="mr-3 text-2xl sm:text-3xl md:text-4xl sm:mr-4">
                    {typeObj.type === "breakfast" ? "🌞" : typeObj.type === "lunch" ? "🍛" : typeObj.type === "dinner" ? "🌙" : typeObj.type === "other" ? "🍪" : "🍽️"}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 capitalize sm:text-xl md:text-2xl">{typeObj.type}</h3>
                </div>
                <p className="mb-3 text-sm font-bold text-gray-800 sm:text-base">Order Before: {timings[typeObj.type.charAt(0).toUpperCase() + typeObj.type.slice(1)] || 'N/A'}</p>
                {currentBlockedMealTypes.has(typeObj.type) && (
                  <p className="mb-3 text-sm font-bold text-red-700">
                    {formatMealType(typeObj.type)} food is not deliverable today for the selected address.
                  </p>
                )}

                {/* Menu Grid */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-6 justify-items-center">
              {menuData[typeObj.type]
  .filter(item => item.Item_Desc1.toLowerCase().includes(searchTerm.toLowerCase()))
  .map((item, idx) => (
    <motion.div
      key={item.ItemId}
      initial={{ opacity: 0, y: 25 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8 + idx * 0.08, duration: 0.5 }}
      className="w-full max-w-md"
    >
      <MenuItemCard
        item={item}
        dateStr={dateStr}
        isOrderAllowed={typeObj.isOrderAllowed && !currentBlockedMealTypes.has(typeObj.type)}
        rainRestrictionMessage={
          currentBlockedMealTypes.has(typeObj.type)
            ? `${formatMealType(typeObj.type)} food is not deliverable today for the selected address.`
            : ""
        }
      />
    </motion.div>
))}
                </div>
              </motion.div>
            ) : null
          );
        })()
      )}
      </motion.div>
    </div>
  );
}
